import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import Modal from '../../ui/Modal';
import {
  mcpService,
  type ManagedMcpIntegration,
  type McpToolDefinition,
} from '../../../services/mcpService';
import { resolveMcpProviderUiConfig } from './mcp-providers/registry';
import { Surface } from '../../ui/Surface';
import { Table } from '../../ui/Table';

const OAUTH_POPUP_EVENT = 'mcp_oauth_complete';
const OAUTH_STORAGE_KEY = 'mcp_oauth_last_complete_at';

export type ConnectionFormState = {
  environment: string;
  baseUrl: string;
  bearerToken: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthRedirectUri: string;
  webhookSigningSecret: string;
  extraAuthValues: Record<string, string>;
};

export const defaultConnectionFormState: ConnectionFormState = {
  environment: 'production',
  baseUrl: '',
  bearerToken: '',
  oauthClientId: '',
  oauthClientSecret: '',
  oauthRedirectUri: '',
  webhookSigningSecret: '',
  extraAuthValues: {},
};

type Props = {
  integration: ManagedMcpIntegration;
  agentId: string;
  onChanged: () => Promise<void> | void;
};

/** Opens a popup and calls `onDone` when it closes (via postMessage, storage event, or poll). */
function watchPopup(popup: Window, onDone: () => Promise<void>) {
  let handled = false;
  let timer: number | undefined;

  const finalize = async () => {
    if (handled) return;
    handled = true;
    await onDone();
  };

  const cleanup = () => {
    if (timer !== undefined) window.clearInterval(timer);
    window.removeEventListener('message', onMessage);
    window.removeEventListener('storage', onStorage);
  };

  const onMessage = async (e: MessageEvent) => {
    if (e.origin !== window.location.origin || e.data?.type !== OAUTH_POPUP_EVENT) return;
    cleanup();
    await finalize();
  };

  const onStorage = async (e: StorageEvent) => {
    if (e.key !== OAUTH_STORAGE_KEY) return;
    cleanup();
    await finalize();
  };

  timer = window.setInterval(async () => {
    if (!popup.closed) return;
    cleanup();
    await finalize();
  }, 1000);

  window.addEventListener('message', onMessage);
  window.addEventListener('storage', onStorage);
}

const McpIntegrationTile: React.FC<Props> = ({ integration, agentId, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [openTools, setOpenTools] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);
  const [tools, setTools] = useState<McpToolDefinition[]>([]);
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});

  const providerUi = resolveMcpProviderUiConfig(integration);

  const [draft, setDraft] = useState<ConnectionFormState>(() => {
    const authConfig = integration.connection?.authConfig || {};
    const transportConfig = integration.connection?.transportConfig || {};
    const str = (v: unknown) => (typeof v === 'string' ? v : '');

    const extraAuthValues: Record<string, string> = {};
    for (const field of providerUi.extraAuthFields || []) {
      const value = str(authConfig[field.key]);
      if (value) extraAuthValues[field.key] = value;
    }

    return {
      ...defaultConnectionFormState,
      environment: integration.connection?.environment || 'production',
      baseUrl: str(transportConfig.url),
      bearerToken: str(authConfig.bearerToken),
      oauthClientId: str(authConfig.clientId),
      oauthClientSecret: str(authConfig.clientSecret),
      oauthRedirectUri: str(authConfig.redirectUri),
      webhookSigningSecret: str(authConfig.hubspotWebhookSigningSecret),
      extraAuthValues,
    };
  });

  const isConnected = Boolean(integration.connection?.isEnabled);
  const authScheme = (integration.authScheme || '').toLowerCase();
  const isOauth = authScheme === 'oauth2';
  const platformOAuthReady = Boolean(integration.platformOAuthReady);
  const isAdapterHttp = integration.transportType === 'adapter_http';
  const canStartConnection = isConnected || integration.toolCount > 0 || isOauth || isAdapterHttp;
  const requiresToken = authScheme !== '' && authScheme !== 'none' && !isOauth;
  const showEndpointField = integration.transportType !== 'stdio' && providerUi.showEndpointOverride !== false;
  const requireClientId = providerUi.requireClientId ?? true;
  const requireClientSecret = providerUi.requireClientSecret ?? true;
  const isHubspotUi = providerUi.providerCode === 'hubspot';
  const isAdminConsentUi = providerUi.useAdminConsent === true;

  const oauthRedirectDisplay =
    draft.oauthRedirectUri.trim() || `${window.location.origin}/oauth/mcp/callback`;

  const apiBase = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  const profileIdForWebhook = integration.connection?.profileId;
  const hubspotWebhookTargetUrl = `${apiBase || '{your API base URL}'}/core/webhooks/hubspot/crm?profileId=${profileIdForWebhook ? encodeURIComponent(profileIdForWebhook) : '{your-connection-profile-id}'}`;

  const statusBadgeClass = isConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600';

  const patchDraft = (patch: Partial<ConnectionFormState>) =>
    setDraft((cur) => ({ ...cur, ...patch }));

  const toggleSecretVisibility = (key: string) =>
    setVisibleSecrets((cur) => ({ ...cur, [key]: !cur[key] }));

  const secretFieldProps = (key: string) => ({
    type: visibleSecrets[key] ? 'text' : 'password',
    rightIcon: visibleSecrets[key] ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />,
    onRightIconClick: () => toggleSecretVisibility(key),
    rightIconLabel: visibleSecrets[key] ? 'Hide value' : 'Show value',
  });

  const openPopup = (url: string, name: string) => {
    const popup = window.open(url, name, 'width=620,height=760');
    if (!popup) {
      toast.error('Popup blocked. Please allow popups and try again.');
      return null;
    }
    return popup;
  };

  const loadTools = async () => {
    setLoadingTools(true);
    try {
      const result = await mcpService.listManagedIntegrationTools(agentId, integration.id);
      setTools(result.items || []);
    } catch (error: any) {
      toast.error(error?.message || `Failed to load tools for ${integration.name}`);
    } finally {
      setLoadingTools(false);
    }
  };

  const handleSaveWebhookSigningSecret = async () => {
    if (!isHubspotUi || !isOauth || !integration.connection?.profileId) {
      toast.error('Connect with HubSpot first so a connection profile exists');
      return;
    }
    if (!draft.webhookSigningSecret.trim()) {
      toast.error('Enter the webhook signing secret');
      return;
    }
    setSubmitting(true);
    try {
      await mcpService.connectManagedIntegration(agentId, integration.id, {
        environment: draft.environment.trim() || 'production',
        authConfig: { hubspotWebhookSigningSecret: draft.webhookSigningSecret.trim() },
      });
      toast.success('Webhook signing secret saved on your connection');
      await onChanged();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save webhook signing secret');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConnect = async () => {
    setSubmitting(true);
    try {
      const extraAuth = Object.fromEntries(
        Object.entries(draft.extraAuthValues).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()])
      );
      await mcpService.connectManagedIntegration(agentId, integration.id, {
        environment: draft.environment.trim() || 'production',
        transportConfig: showEndpointField && draft.baseUrl.trim() ? { url: draft.baseUrl.trim() } : {},
        authConfig: {
          ...(requiresToken && draft.bearerToken.trim() ? { bearerToken: draft.bearerToken.trim() } : {}),
          ...extraAuth,
        },
      });
      toast.success(`${integration.name} connected`);
      setOpen(false);
      await onChanged();
    } catch (error: any) {
      toast.error(error?.message || `Failed to connect ${integration.name}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAuthorize = async () => {
    const redirectUri = draft.oauthRedirectUri.trim() || `${window.location.origin}/oauth/mcp/callback`;
    if (!platformOAuthReady && requireClientId && !draft.oauthClientId.trim()) {
      toast.error('Client ID is required for this OAuth provider');
      return;
    }
    if (!platformOAuthReady && requireClientSecret && !draft.oauthClientSecret.trim()) {
      toast.error('Client Secret is required for this OAuth provider');
      return;
    }

    setSubmitting(true);
    try {
      const result = await mcpService.startManagedOAuth(agentId, integration.id, {
        environment: draft.environment.trim() || 'production',
        transportConfig: showEndpointField && draft.baseUrl.trim() ? { url: draft.baseUrl.trim() } : {},
        authConfig: {
          clientId: platformOAuthReady ? undefined : draft.oauthClientId.trim() || undefined,
          clientSecret: platformOAuthReady ? undefined : draft.oauthClientSecret.trim() || undefined,
          clientName: providerUi.supportsDynamicClientRegistration ? `${integration.name} MCP Client` : undefined,
          redirectUri,
          scopes: [],
          ...(isHubspotUi && draft.webhookSigningSecret.trim()
            ? { hubspotWebhookSigningSecret: draft.webhookSigningSecret.trim() }
            : {}),
        },
      });

      const popup = openPopup(result.authorizeUrl, 'mcp_oauth_popup');
      if (!popup) return;
      toast.info('Complete authorization in popup. This tile will refresh when popup closes.');
      watchPopup(popup, async () => { setOpen(false); await onChanged(); });
    } catch (error: any) {
      toast.error(error?.message || `Failed to start OAuth for ${integration.name}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminConsent = async () => {
    const clientId = (integration.metadata as any)?.oauthClientId;
    if (!clientId) {
      toast.error('Platform OAuth Client ID is not configured. Contact your administrator.');
      return;
    }

    const redirectUri = `${window.location.origin}/oauth/mcp/callback`;
    // 'common' lets Microsoft detect the admin's tenant from their login session.
    // The actual tenantId comes back in the callback and is stored there.
    const consentUrl =
      `https://login.microsoftonline.com/common/adminconsent` +
      `?client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&state=${encodeURIComponent(agentId)}`;

    const popup = openPopup(consentUrl, 'ms365_admin_consent');
    if (!popup) return;
    toast.info('Complete admin consent in the popup. This tile will refresh when done.');
    watchPopup(popup, async () => { setOpen(false); await onChanged(); });
  };

  const handleDisconnect = async () => {
    setSubmitting(true);
    try {
      await mcpService.disconnectManagedIntegration(agentId, integration.id);
      toast.success(`${integration.name} disconnected`);
      setOpen(false);
      await onChanged();
    } catch (error: any) {
      toast.error(error?.message || `Failed to disconnect ${integration.name}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Surface as="section" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-xl font-semibold text-slate-900">{integration.name}</h4>
              <span className={`rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wide ${statusBadgeClass}`}>
                {isConnected ? 'Connected' : 'Not connected'}
              </span>
            </div>
            {integration.description && (
              <p className="mt-2 text-sm text-slate-500">{integration.description}</p>
            )}
          </div>
          <div className="rounded-2xl bg-slate-50 px-4 py-3 text-right">
            <p className="text-xs uppercase tracking-wide text-slate-400">Available tools</p>
            <p className="mt-1 text-lg font-semibold text-slate-900">{integration.toolCount}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">{integration.transportType}</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">{integration.authScheme || 'no-auth'}</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">{integration.status}</span>
        </div>

        {integration.connection && (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            <p className="font-medium text-slate-900">Current connection</p>
            <p className="mt-1">Status: {integration.connection.status}</p>
            <p>Environment: {integration.connection.environment || 'production'}</p>
            {integration.connection.lastValidatedAt && (
              <p>Last validated: {new Date(integration.connection.lastValidatedAt).toLocaleString()}</p>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => { setOpenTools(true); loadTools(); }}>
            View Tools
          </Button>
          <Button onClick={() => setOpen(true)} disabled={!canStartConnection}>
            {isConnected ? 'Configure' : 'Connect'}
          </Button>
        </div>

        {integration.toolCount === 0 && (
          <p className="mt-3 text-sm text-amber-600">This integration has no discovered tools yet.</p>
        )}
      </Surface>

      <Modal
        isOpen={open}
        onClose={() => !submitting && setOpen(false)}
        title={`${integration.name} Connection`}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Environment"
            value={draft.environment}
            onChange={(e) => patchDraft({ environment: e.target.value })}
            placeholder="production"
          />
          {showEndpointField && (
            <Input
              label={providerUi.endpointOverrideLabel || 'Endpoint Override'}
              value={draft.baseUrl}
              onChange={(e) => patchDraft({ baseUrl: e.target.value })}
              placeholder={providerUi.endpointOverridePlaceholder || 'Optional custom endpoint'}
              helperText={providerUi.endpointOverrideHelpText}
            />
          )}
          {requiresToken && (
            <Input
              label="Access Token"
              value={draft.bearerToken}
              onChange={(e) => patchDraft({ bearerToken: e.target.value })}
              placeholder="Paste your token"
              helperText="Default bearer token used for authenticated calls unless a specific action overrides it."
              {...secretFieldProps('bearerToken')}
            />
          )}
          {providerUi.extraAuthFields?.map((field) => (
            <Input
              key={field.key}
              label={field.label}
              value={draft.extraAuthValues[field.key] ?? ''}
              onChange={(e) => patchDraft({ extraAuthValues: { ...draft.extraAuthValues, [field.key]: e.target.value } })}
              placeholder={field.placeholder}
              helperText={field.helperText}
              {...(field.type === 'password' ? secretFieldProps(field.key) : { type: field.type || 'text' })}
            />
          ))}
          {integration.metadata && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Server Metadata</span>
                <span className="text-xs text-slate-400">Read-only — edit in MCP Catalogue (super admin)</span>
              </div>
              <pre className="max-h-48 overflow-auto rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                {JSON.stringify(integration.metadata, null, 2)}
              </pre>
            </div>
          )}
          {isOauth && (
            <>
              {isAdminConsentUi ? (
                <p className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  Click <strong>{providerUi.adminConsentButtonLabel || 'Authorize for my Organization'}</strong> to open a Microsoft login popup.
                  {' '}A <strong>Global Administrator</strong> of your Microsoft 365 organization must sign in and approve the permissions.
                  {' '}Once approved, all staff calendars in your organization become accessible for booking.
                </p>
              ) : platformOAuthReady ? (
                <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  This integration uses your organization&apos;s OAuth app (configured by an admin). Click Authorize to connect.
                </p>
              ) : (
                <>
                  <Input
                    label={providerUi.oauthClientIdLabel || 'OAuth Client ID'}
                    value={draft.oauthClientId}
                    onChange={(e) => patchDraft({ oauthClientId: e.target.value })}
                    placeholder="Provider app client id"
                    helperText={requireClientId ? 'Required' : 'Optional for providers using dynamic client registration (DCR).'}
                  />
                  <Input
                    label={providerUi.oauthClientSecretLabel || 'OAuth Client Secret'}
                    value={draft.oauthClientSecret}
                    onChange={(e) => patchDraft({ oauthClientSecret: e.target.value })}
                    placeholder="Provider app client secret"
                    helperText={requireClientSecret ? 'Required' : 'Optional for providers using PKCE public clients.'}
                    {...secretFieldProps('oauthClientSecret')}
                  />
                </>
              )}
              {!isAdminConsentUi && (
                platformOAuthReady ? (
                  <div className="text-sm text-slate-600">
                    <span className="font-medium text-slate-800">Redirect URI</span>
                    <p className="mt-1 break-all font-mono text-xs text-slate-700">{oauthRedirectDisplay}</p>
                    <p className="mt-1 text-xs text-slate-500">This URL must be allowed in your HubSpot app settings.</p>
                  </div>
                ) : (
                  <Input
                    label="OAuth Redirect URI"
                    value={draft.oauthRedirectUri}
                    onChange={(e) => patchDraft({ oauthRedirectUri: e.target.value })}
                    placeholder={`${window.location.origin}/oauth/mcp/callback`}
                  />
                )
              )}
              {isHubspotUi && (
                <>
                  <Input
                    label="Webhook signing secret"
                    value={draft.webhookSigningSecret}
                    onChange={(e) => patchDraft({ webhookSigningSecret: e.target.value })}
                    placeholder="Same secret HubSpot uses to sign webhook payloads"
                    helperText="Stored on this integration's connection profile (mcp_connection_profiles.auth_config). Typically your HubSpot app client secret. Enter before Authorize, or save again after you are connected."
                    {...secretFieldProps('webhookSigningSecret')}
                  />
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                    <p className="font-medium text-amber-900">Sync contacts into Leads (webhook)</p>
                    <p className="mt-2 text-amber-900/90">
                      MCP does not push HubSpot changes by itself. To create or update a lead in our database whenever a
                      HubSpot contact is created or updated:
                    </p>
                    <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-amber-900/90">
                      <li>Enter the <strong>Webhook signing secret</strong> above (same value HubSpot uses for signature verification).</li>
                      <li>Connect with <strong>Authorize</strong>. The webhook URL below then includes your connection profile id.</li>
                      <li>In HubSpot: your app → Webhooks → create a subscription for <strong>Contacts</strong> with{' '}
                        <code>contact.creation</code> and <code>contact.propertyChange</code> (e.g. email, firstname, lastname, company).
                      </li>
                      <li>Set the target URL exactly to: <code className="break-all text-xs">{hubspotWebhookTargetUrl}</code></li>
                    </ol>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          {isConnected && (
            <Button variant="secondary" onClick={handleDisconnect} disabled={submitting}>
              Disconnect
            </Button>
          )}
          {isHubspotUi && isOauth && isConnected && (
            <Button variant="secondary" onClick={handleSaveWebhookSigningSecret} disabled={submitting}>
              Save webhook secret
            </Button>
          )}
          {isOauth && isAdminConsentUi ? (
            <Button onClick={handleAdminConsent} disabled={submitting}>
              {providerUi.adminConsentButtonLabel || 'Authorize for my Organization'}
            </Button>
          ) : isOauth ? (
            <Button variant="secondary" onClick={handleAuthorize} disabled={submitting || !canStartConnection}>
              {isConnected ? 'Re-authorize' : 'Authorize'}
            </Button>
          ) : (
            <Button onClick={handleConnect} disabled={submitting || !canStartConnection}>
              {isConnected ? 'Reconnect' : 'Connect'}
            </Button>
          )}
        </div>

        {isOauth && !isAdminConsentUi && (
          <p className="mt-3 text-sm text-slate-500">
            OAuth integrations use the Authorize button. Connection is finalized after OAuth callback.
          </p>
        )}
        {isOauth && providerUi.supportsDynamicClientRegistration && (
          <p className="mt-2 text-sm text-slate-500">
            This provider supports dynamic client registration. You can leave client credentials blank.
          </p>
        )}
      </Modal>

      <Modal
        isOpen={openTools}
        onClose={() => !loadingTools && setOpenTools(false)}
        title={`${integration.name} Tools`}
        size="xl"
      >
        {loadingTools ? (
          <div className="text-sm text-slate-500">Loading tools...</div>
        ) : !tools.length ? (
          <div className="text-sm text-slate-500">No tools discovered yet for this MCP server.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="w-full min-w-[720px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="px-3 py-2 font-medium">Tool Name</th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Risk</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((tool) => (
                  <tr key={tool.id} className="border-b border-slate-100 align-top">
                    <td className="px-3 py-2 font-medium text-slate-900">{tool.toolName}</td>
                    <td className="px-3 py-2 text-slate-700">{tool.title || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{tool.status}</td>
                    <td className="px-3 py-2 text-slate-700">{tool.riskLevel || 'standard'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={loadTools} disabled={loadingTools}>Refresh</Button>
          <Button variant="ghost" onClick={() => setOpenTools(false)} disabled={loadingTools}>Close</Button>
        </div>
      </Modal>
    </>
  );
};

export default McpIntegrationTile;
